import { alpha, type Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

const usePlaylistItemStyles = makeStyles((theme: Theme) => ({
  '@keyframes flashHighlight': {
    '0%': { backgroundColor: alpha(theme.palette.secondary.main, 0.31) },
    '50%': { backgroundColor: alpha(theme.palette.secondary.main, 0.15) },
    '100%': { backgroundColor: 'transparent' },
  },

  '@keyframes skeletonPulse': {
    '0%': { opacity: 0.4 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.4 },
  },

  flashRow: {
    animation: '$flashHighlight 1.5s ease-out',
  },

  dropZoneActive: {
    backgroundColor: alpha(theme.palette.secondary.main, 0.21),
    boxShadow: `inset 0 0 0 2px ${alpha(theme.palette.secondary.main, 0.5)}`,
    borderRadius: 4,
    transition: 'all 150ms ease',
  },

  pendingSkeleton: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '0 12px',
    gap: theme.spacing(1),
    width: '100%',
    borderBottom: `1px solid ${theme.palette.divider}`,
    animation: '$skeletonPulse 1.2s ease-in-out infinite',
    backgroundColor: alpha(theme.palette.secondary.main, 0.08),
  },

  compactRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '0 12px',
    gap: theme.spacing(1),
    width: '100%',
    border: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: 'none',
    color: 'inherit',
    font: 'inherit',
    cursor: 'pointer',
    textAlign: 'left' as const,
    transition: 'background-color 150ms',
    position: 'relative' as const,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover $hoverVisible': {
      opacity: 1,
    },
  },

  dragPlaceholder: {
    minHeight: 4,
    width: '100%',
    backgroundColor: theme.palette.secondary.main,
    borderRadius: 2,
    opacity: 0.6,
  },

  hoverVisible: {
    opacity: 0,
    transition: 'opacity 150ms',
  },

  indexNumber: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    minWidth: 16,
    textAlign: 'right' as const,
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
  },

  compactThumbnail: {
    width: 36,
    height: 24,
    flexShrink: 0,
    borderRadius: theme.shape.borderRadius / 2,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },

  mediaTypeIcon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    width: 18,
    textAlign: 'center' as const,
  },

  itemTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: 12,
    lineHeight: '40px',
    cursor: 'pointer',
    color: theme.palette.text.primary,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    textAlign: 'left' as const,
    '&:hover': {
      color: theme.palette.secondary.dark,
    },
  },

  removeButton: {
    cursor: 'pointer',
    flexShrink: 0,
    padding: theme.spacing(0.25),
    fontSize: 14,
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.error.main,
    },
  },

  dragGrip: {
    cursor: 'move',
    fontSize: 14,
    color: theme.palette.text.disabled,
    flexShrink: 0,
  },

  playlistItemCount: {
    fontSize: 10,
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.secondary,
    padding: '2px 6px',
    borderRadius: 8,
    flexShrink: 0,
  },

  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    padding: '0 12px',
    gap: theme.spacing(1),
    width: '100%',
  },

  thinScrollbar: {
    // Firefox
    scrollbarWidth: 'thin' as const,
    scrollbarColor: `${theme.palette.action.hover} transparent`,
    // WebKit / Blink
    '&::-webkit-scrollbar': {
      width: 4,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.action.disabled,
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: theme.palette.action.active,
    },
  },
}));

export default usePlaylistItemStyles;
